import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks';
import {
	preloadSideNavigation,
	preloadSideNavigationNeedsContentId,
} from '@confluence/side-navigation/entry-points/preloadSideNavigation';
import { preloadSpaceGuestList } from '@confluence/space-guest-list/entry-points/preloadSpaceGuestList';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData';
import { preloadEditorOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadEditorOnboardingState';
import { preloadPageTree } from '@confluence/page-tree/entry-points/preloadPageTree';
import { preloadBlogTree } from '@confluence/blog-tree/entry-points/preloadBlogTree';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points';
import { PAGE_TREE_STATUSES } from '@confluence/page-tree/entry-points/pageTreeStatuses';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools';
import type { RouteMatch } from '@confluence/route';
import { preloadDraft } from '@confluence/fabric-providers/entry-points/preloadDraft';
import { preloadBreadcrumbs } from '@confluence/breadcrumbs/entry-points/preloadBreadcrumbs';
import { preloadClassificationContextForEditContent } from '@confluence/data-classification/entry-points/queries';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken';
import { preloadSpaceViews } from '@confluence/space-views/entry-points/preloadSpaceViews';
import { preloadSuggestedTemplateQuery } from '@confluence/blank-page-quick-actions/entry-points/preloadSuggestedTemplateQuery';
import { fg } from '@confluence/feature-gating';

import { getIsEmbeddedConfluence } from './matchHelpers';
import { matchEditBlog } from './matchRoutes';

export const preloadEditPageRoute = async (
	match: RouteMatch,
	_url: string,
	isTransition: boolean = false,
) => {
	const { userId, isLicensed, cloudId } = await getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { contentId, spaceKey } = match.params;

	const isSpaceAliasFFEnabled = true;
	const isBlog = Boolean(matchEditBlog(_url));

	const shouldPreloadMentionsProvider = Boolean(process.env.REACT_SSR);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const shouldPreloadNavigation = !isEmbeddedConfluence;

	tasks.push(
		preloadBreadcrumbs(spaceKey, contentId, false, isSpaceAliasFFEnabled),
		preloadEditorData({
			spaceKey,
			userId,
			contentId,
			cloudId,
			preloadNativeCollabData: true,
			shouldPreloadMentionsProvider,
			shouldPreloadNativeEditorData: false,
			isBlog,
			isTransition,
		}),
	);

	if (spaceKey && fg('confluence_preload_blank_page_quick_actions')) {
		tasks.push(preloadSuggestedTemplateQuery({ spaceKey }));
	}

	if (isLicensed) {
		tasks.push(preloadEditorOnboardingState());
	}

	if (isTransition) {
		return Promise.all(tasks);
	}

	if (process.env.REACT_SSR) {
		// Navigation and page tree
		if (shouldPreloadNavigation) {
			tasks.push(
				preloadSideNavigation(spaceKey),
				preloadSideNavigationNeedsContentId(spaceKey, contentId),
				preloadSpaceGuestList(spaceKey),
				preloadShortcutsSmartLinks(spaceKey),
				preloadSpaceViews(spaceKey, isLicensed),
				preloadPageTree({
					spaceKey,
					statuses: PAGE_TREE_STATUSES,
					isSuperAdmin: false,
					contentId,
				}),
				preloadBlogTree({ spaceKey }),
			);
		}

		tasks.push(
			preloadMediaToken(contentId),
			preloadClassificationContextForEditContent(contentId, spaceKey),
		);
	} else {
		tasks.push(preloadDraft(contentId));
	}

	tasks.push(preloadSpaceDetail(spaceKey));

	return Promise.all(tasks);
};
